import type { MouseEventHandler, ReactNode, RefObject } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { Box } from '@mui/material';

import Typography from '@/shared/ui/typography/Text';

const Header = ({
  icon,
  title,
  subtitle,
  headerAction,
  before,
  historyButtonRef,
  onClose,
  onHistoryClick,
  isHistoryActive
}: {
  onClose: MouseEventHandler;
  title: string;
  subtitle?: ReactNode;
  icon: ReactNode;
  before?: ReactNode;
  historyButtonRef?: RefObject<HTMLElement | null>;
  headerAction?: ReactNode;
  onHistoryClick?: () => void;
  isHistoryActive?: boolean;
}) => {
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        gap: '16px',
        alignItems: 'stretch',
        borderBottom: '1px solid',
        borderBottomColor: 'gx.textDisabled'
      }}
    >
      {before}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flex: 1,
          pl: before ? 0 : '24px',
          pr: '32px'
        }}
      >
        <Box>
          <Typography
            component='div'
            sx={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
              fontSize: '32px',
              height: '60px'
            }}
            variant='h2'
          >
            {icon}
            {title}
          </Typography>
          {subtitle && (
            <Typography
              component='div'
              sx={{
                color: 'gx.textDisabled',
                fontFamily: 'Cairo',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: 'normal'
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {headerAction}
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: '2px'
        }}
      >
        <Box
          ref={historyButtonRef}
          role='button'
          tabIndex={0}
          sx={{
            width: 48,
            height: 108,
            backgroundColor: 'gx.tileDark',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: onHistoryClick ? 'pointer' : 'default',
            p: 0,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '3px',
              backgroundColor: isHistoryActive ? 'gx.highlight' : 'transparent'
            }
          }}
          onClick={onHistoryClick}
        >
          <HistoryOutlinedIcon />
        </Box>
        <Box
          role='button'
          tabIndex={0}
          sx={{
            width: 48,
            height: 108,
            backgroundColor: 'gx.tileDark',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer'
          }}
          onClick={onClose}
        >
          <CloseIcon />
        </Box>
      </Box>
    </Box>
  );
};

export default Header;
